import config from 'config'
import bus from 'bus'

import ConsentService from 'services/ConsentService'
import { enablePosthog, posthogClient } from 'plugins/posthog'

/**
 * Cookie consent — client half.
 *
 * The server decides the state (stored choice + country, see ConsentTracking)
 * and ships it in the page config, so this module never re-derives it: it only
 * records changes and switches the tags on for the current page view. On a
 * fresh page load the server has already done the equivalent server-side.
 *
 * Ahoy and Sentry are deliberately not gated here — they are first-party and
 * always on.
 */

// Falls back to "allowed" so a config without a consent block (e.g. an
// unexpected render path) never silently kills analytics.
const state = { required: false, decided: true, analytics: true, marketing: true, gpc: false, blocking: false, ...(config.consent || {}) }

// Cookies and Web Storage the tags set on our own domain, by the consent
// category they belong to. When a category is denied, its storage goes too —
// the vendors cannot delete it for us.
const ANALYTICS_STORAGE_PREFIXES = ['_ga', '_gid', 'ph_', '__ph']
const MARKETING_STORAGE_PREFIXES = ['_gcl', '_gac']

export function consentState() {
  return { ...state }
}

export function needsConsentBanner() {
  return state.required && !state.decided
}

export async function saveConsent({ analytics, marketing, choice }) {
  const revoked = (state.analytics && !analytics) || (state.marketing && !marketing)

  // Only the server writes the consent cookie, so a failed request means no
  // record and no cookie: the tags stay as they are and the visitor is asked
  // again on the next page load.
  await ConsentService.save({ analytics, marketing, choice, url: window.location.pathname })

  state.analytics = analytics
  state.marketing = marketing
  state.decided = true

  bus.$emit('consent:changed', consentState())
  updateGoogleConsent()

  if (revoked) {
    // Tags already running in this tab cannot be fully unloaded, so reload into
    // a clean page instead of pretending they stopped. The sweep here is best
    // effort — a dying tag can re-write its storage during unload (PostHog's
    // pageleave capture does) — so the boot-time sweep in applyConsent() is
    // what actually guarantees a denied category's storage is gone.
    posthogClient()?.opt_out_capturing()
    clearDeniedStorage()
    window.location.reload()

    return
  }

  applyConsent()
}

// Safe to run on every page load: the server already rendered the page for this
// state, so each step here is a no-op unless the choice changed in this tab.
export function applyConsent() {
  // Boot-time janitor for denied categories. Running before any tag starts is
  // the only reliable moment to sweep: nothing can re-write what it left
  // behind, and it also removes storage that predates a visitor's rejection.
  clearDeniedStorage()

  // GTM is always rendered by the server (advanced consent mode); the consent
  // defaults and updateGoogleConsent() decide what it may store.
  if (state.analytics) {
    enablePosthog()
  }
}

function updateGoogleConsent() {
  if (typeof window.gtag !== 'function') return

  const ads = state.marketing ? 'granted' : 'denied'

  window.gtag('consent', 'update', {
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
    analytics_storage: state.analytics ? 'granted' : 'denied'
  })
}

function clearDeniedStorage() {
  const prefixes = [...(state.analytics ? [] : ANALYTICS_STORAGE_PREFIXES), ...(state.marketing ? [] : MARKETING_STORAGE_PREFIXES)]

  if (!prefixes.length) return

  const matches = (name) => prefixes.some((prefix) => name.startsWith(prefix))
  const domain = window.location.hostname.replace(/^www\./, '')

  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim()

    if (!matches(name)) return

    document.cookie = `${name}=; Max-Age=0; path=/`
    document.cookie = `${name}=; Max-Age=0; path=/; domain=.${domain}`
  })

  // PostHog also persists the distinct/device id in Web Storage — withdrawing
  // consent has to take the identifier, not just the cookie copy of it.
  ;[localStorage, sessionStorage].forEach((storage) => {
    Object.keys(storage)
      .filter(matches)
      .forEach((key) => storage.removeItem(key))
  })
}
